import { create } from "zustand";
import { stripFilterTag } from "../chat/filters";
import { readSse } from "../chat/sse";
import { getCsrfToken } from "../state/csrf";
import { ChatSessionSummary, FilterSuggestion, WorkspaceMessage } from "./types";

const STREAM_FAILED = "Er ging iets mis tijdens chatten. Probeer het later opnieuw.";

// Buiten de store: een AbortController hoort niet in renderbare state.
let controller: AbortController | null = null;

const json = (method: string, body?: any): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
  ...(body ? { body: JSON.stringify(body) } : {}),
});

interface WorkspaceState {
  projectId?: number;
  sessions: ChatSessionSummary[];
  activeSessionId?: number;
  // Door de server aangemaakt tijdens het streamen; de view zet hem in de URL
  // en wist hem daarna weer.
  newSessionId?: number;
  messages: WorkspaceMessage[];
  isStreaming: boolean;
  error?: string;
  openProject: (projectId: number) => void;
  loadSessions: () => Promise<ChatSessionSummary[]>;
  loadSession: (sessionId?: number) => Promise<boolean>;
  newSession: () => void;
  clearNewSession: () => void;
  sendMessage: (content: string) => Promise<void>;
  stop: () => void;
  renameSession: (sessionId: number, title: string) => Promise<void>;
  deleteSession: (sessionId: number) => Promise<void>;
}

export const useWorkspace = create<WorkspaceState>((set, get) => ({
  sessions: [],
  messages: [],
  isStreaming: false,

  openProject: (projectId: number) => {
    if (get().projectId === projectId) return;
    set({ projectId, sessions: [], messages: [], activeSessionId: undefined, error: undefined });
  },

  loadSessions: async () => {
    const { projectId } = get();
    if (!projectId) return [];
    const response = await fetch(`/api/projects/${projectId}/chat/sessions`);
    if (!response.ok) return [];
    const sessions: ChatSessionSummary[] = await response.json();
    set({ sessions });
    return sessions;
  },

  // Returns false als de sessie niet bestaat of niet bij dit project hoort.
  loadSession: async (sessionId?: number) => {
    const { projectId } = get();
    if (!projectId || !sessionId) {
      set({ activeSessionId: undefined, messages: [] });
      return true;
    }
    const response = await fetch(`/api/projects/${projectId}/chat/sessions/${sessionId}`);
    if (!response.ok) {
      set({ activeSessionId: undefined, messages: [] });
      return false;
    }
    const detail = await response.json();
    set({ activeSessionId: detail.id, messages: detail.messages, error: undefined });
    return true;
  },

  newSession: () =>
    set({ activeSessionId: undefined, messages: [], error: undefined, newSessionId: undefined }),
  clearNewSession: () => set({ newSessionId: undefined }),

  sendMessage: async (content: string) => {
    const { projectId, activeSessionId, isStreaming } = get();
    if (!projectId || isStreaming) return;

    set((state) => ({
      messages: [
        ...state.messages,
        { role: "user", content, finished: true, filters: [] },
        { role: "assistant", content: "", finished: true, filters: [] },
      ],
      isStreaming: true,
      error: undefined,
    }));

    // Wijzigt het laatste bericht (altijd de assistent-rij die we net toevoegden).
    const patchAnswer = (change: (message: WorkspaceMessage) => WorkspaceMessage) =>
      set((state) => {
        const messages = [...state.messages];
        const idx = messages.length - 1;
        if (idx >= 0 && messages[idx].role === "assistant") messages[idx] = change(messages[idx]);
        return { messages };
      });

    // Ruwe tekst apart bijhouden: de <filters>-tag mag niet in beeld komen,
    // maar de server strip't hem pas bij het opslaan.
    let raw = "";
    controller = new AbortController();
    try {
      const response = await fetch(`/api/projects/${projectId}/chat/message`, {
        ...json("POST", { content, session_id: activeSessionId }),
        signal: controller.signal,
      });

      // De SSE-events komen uit een plain Django view en zijn snake_case; de
      // ninja-endpoints leveren camelCase. Hier is de enige plek waar dat
      // verschil bestaat — daarbuiten praat alles camelCase.
      await readSse(response, (data) => {
        if (data.session_id) {
          set((state) => ({
            activeSessionId: data.session_id,
            // Alleen als de server hem net heeft aangemaakt: de view zet hem dan
            // in de URL. Sturen op "store en URL lopen uiteen" zou ook vuren als
            // je zelf een nieuwe chat begint, en je dan terugkaatsen.
            newSessionId: activeSessionId ? undefined : data.session_id,
            messages: state.messages.map((m, i) =>
              i === state.messages.length - 1 ? { ...m, id: data.assistant_message_id } : m
            ),
          }));
          get().loadSessions();
          return;
        }
        if (data.content) {
          raw += data.content;
          const shown = stripFilterTag(raw);
          patchAnswer((m) => ({ ...m, content: shown }));
          return;
        }
        if (data.filter) {
          const filter: FilterSuggestion = {
            id: data.filter.id,
            criteria: data.filter.criteria,
            label: data.filter.label,
            total: data.filter.total,
            capped: data.filter.capped,
            created: data.filter.created,
          };
          patchAnswer((m) => ({ ...m, filters: [...m.filters, filter] }));
          return;
        }
        if (data.assistant_deleted) {
          set((state) => ({ messages: state.messages.slice(0, -1) }));
        }
      });
    } catch (e: any) {
      if (e?.name === "AbortError") {
        // De server bewaart het gedeeltelijke antwoord met finished=False.
        patchAnswer((m) => ({ ...m, finished: false }));
      } else {
        patchAnswer((m) => ({ ...m, content: stripFilterTag(raw) || STREAM_FAILED }));
        set({ error: STREAM_FAILED });
      }
    } finally {
      controller = null;
      set({ isStreaming: false });
      get().loadSessions();
    }
  },

  stop: () => controller?.abort(),

  renameSession: async (sessionId: number, title: string) => {
    const { projectId } = get();
    if (!projectId) return;
    const response = await fetch(
      `/api/projects/${projectId}/chat/sessions/${sessionId}`,
      json("PATCH", { title })
    );
    if (response.ok) get().loadSessions();
  },

  deleteSession: async (sessionId: number) => {
    const { projectId } = get();
    if (!projectId) return;
    const response = await fetch(
      `/api/projects/${projectId}/chat/sessions/${sessionId}`,
      json("DELETE")
    );
    if (!response.ok) return;
    if (get().activeSessionId === sessionId) get().newSession();
    get().loadSessions();
  },
}));
